<template>
  <div class="max-w-[90vw] mx-auto">
    <div class="w-full flex justify-end align-center">
      <v-container class="onest-font">
        <a
          @click.prevent="goBack"
          style="color: inherit; cursor: pointer; text-decoration: none"
          class="d-flex align-center d-inline-flex"
        >
          <v-icon size="20" class="mr-1">mdi-arrow-u-left-bottom</v-icon>
          <span style="text-decoration: underline">voltar</span>
        </a>
        <h1 class="font-bold mt-2 text-5xl">{{ pageTitle }}</h1>
      </v-container>
      <div class="w-full flex justify-end gap-2 align-center">
        <v-btn
          v-if="statusAtual === 'EDICAO'"
          color="primary"
          :disabled="!nomeValidacao || loadingSubmit"
          :loading="loadingSubmit"
          variant="outlined"
          @click="submitValidation('EDICAO')"
          >Salvar Validação</v-btn
        >
        <v-btn
          v-if="statusAtual === 'EDICAO'"
          color="primary"
          :disabled="!isFormValid || loadingSubmit"
          :loading="loadingSubmit"
          @click="submitValidation('PREPARACAO')"
          >Publicar Validação</v-btn
        >
        <v-btn
          v-if="statusAtual !== 'EDICAO'"
          color="primary"
          :disabled="!isFormValid || loadingSubmit"
          :loading="loadingSubmit"
          @click="submitValidation(statusAtual)"
          >Salvar Alterações</v-btn
        >
      </div>
    </div>
    <div v-if="loading" class="py-12 text-center text-gray-500 font-medium">
      <v-progress-circular
        indeterminate
        color="primary"
        class="mb-4"
        size="64"
      ></v-progress-circular>
      <p>Carregando dados da validação...</p>
    </div>
    <v-container class="" v-else>
      <v-form ref="form" v-model="isFormValid">
        <div class="w-full">
          <div>
            <div class="flex justify-between items-center w-full">
              <h2 class="text-2xl font-bold">Informações básicas</h2>
              <span class="text-sm text-gray-500 font-medium"
                ><span class="text-red-500 text-lg">*</span> Campos obrigatórios
                para publicação</span
              >
            </div>
            <div class="grid grid-cols-2 gap-4 mt-2">
              <div>
                <p class="mt-5">
                  Nome da Validação <span class="text-red-500">*</span>
                </p>
                <v-text-field
                  class="mt-2"
                  variant="solo"
                  density="compact"
                  v-model="nomeValidacao"
                  :rules="[rules.required]"
                  placeholder="Ex. POC X - 202x.x"
                ></v-text-field>
              </div>
              <div>
                <p class="mt-5 font-medium">
                  Projetos Envolvidos <span class="text-red-500">*</span>
                </p>
                <div
                  class="mt-2 flex flex-wrap gap-2 items-center min-h-[44px] p-2 border border-gray-300 rounded-lg bg-white"
                >
                  <span
                    v-if="projetosSelecionados.length === 0"
                    class="text-gray-400 text-sm px-2"
                  >
                    Nenhum projeto selecionado
                  </span>
                  <v-chip
                    v-for="projId in projetosSelecionados"
                    :key="projId"
                    color="primary"
                    variant="flat"
                    closable
                    size="small"
                    @click:close="removerProjeto(projId)"
                  >
                    {{ getProjectName(projId) }}
                  </v-chip>
                  <v-btn
                    variant="flat"
                    color="primary"
                    size="small"
                    class="ml-auto rounded-full"
                    prepend-icon="mdi-plus"
                    @click="openProjectModal"
                  >
                    Selecionar
                  </v-btn>
                </div>
                <v-input
                  v-model="projetosSelecionados"
                  :rules="[rules.requiredArray]"
                  class="hide-input-details mt-1"
                ></v-input>
              </div>
            </div>
          </div>
          <div class="w-full mt-4">
            <h2 class="text-2xl font-bold">Cronograma da Validação</h2>
            <h3 class="mt-6 text-lg font-medium">
              Defina as datas finais para:
            </h3>
            <div class="grid grid-cols-2 gap-4 mt-4 w-fit">
              <div class="validation-step">
                <p class="w-72 m-0">
                  1. Preparação <span class="text-red-500">*</span>
                </p>
                <v-text-field
                  class="max-w-[200px]"
                  density="compact"
                  variant="outlined"
                  type="date"
                  :min="minDate"
                  v-model="dataPreparacao"
                  :rules="[rules.required]"
                  hide-details="auto"
                ></v-text-field>
              </div>

              <div class="validation-step">
                <div class="w-72 flex items-center justify-between pr-4">
                  <div class="flex items-center gap-2">
                    <p class="m-0">
                      2. Validação de roteiros
                      <span class="text-red-500">{{
                        validaRoteiros ? '*' : ''
                      }}</span>
                    </p>
                    <v-switch
                      v-model="validaRoteiros"
                      color="primary"
                      hide-details
                      inset
                      density="compact"
                    ></v-switch>
                  </div>
                </div>
                <v-text-field
                  :disabled="!validaRoteiros"
                  class="max-w-[200px]"
                  density="compact"
                  variant="outlined"
                  type="date"
                  :min="minDateValidacaoRoteiros"
                  v-model="dataValidacaoRoteiros"
                  :rules="validaRoteiros ? [rules.required] : []"
                  hide-details="auto"
                ></v-text-field>
              </div>

              <div class="validation-step">
                <p class="w-72 m-0">
                  3. Execução da validação <span class="text-red-500">*</span>
                </p>
                <v-text-field
                  class="max-w-[200px]"
                  density="compact"
                  variant="outlined"
                  type="date"
                  :min="minDateExecucao"
                  v-model="dataExecucao"
                  :rules="[rules.required]"
                  hide-details="auto"
                ></v-text-field>
              </div>

              <div class="validation-step">
                <p class="w-72 m-0">
                  4. Identificação de pendências
                  <span class="text-red-500">*</span>
                </p>
                <v-text-field
                  class="max-w-[200px]"
                  density="compact"
                  variant="outlined"
                  type="date"
                  :min="minDateIdentificacao"
                  v-model="dataIdentificacaoPendencias"
                  :rules="[rules.required]"
                  hide-details="auto"
                ></v-text-field>
              </div>

              <div class="validation-step">
                <p class="w-72 m-0">
                  5. Priorização de pendências
                  <span class="text-red-500">*</span>
                </p>
                <v-text-field
                  class="max-w-[200px]"
                  density="compact"
                  variant="outlined"
                  type="date"
                  :min="minDatePriorizacao"
                  v-model="dataPriorizacaoPendencias"
                  :rules="[rules.required]"
                  hide-details="auto"
                ></v-text-field>
              </div>

              <div class="validation-step">
                <p class="w-72 m-0">
                  6. Correções
                  <span class="text-red-500">*</span>
                </p>
                <v-text-field
                  class="max-w-[200px]"
                  density="compact"
                  variant="outlined"
                  type="date"
                  :min="minDateCorrecoes"
                  v-model="dataCorrecoes"
                  :rules="[rules.required]"
                  hide-details="auto"
                ></v-text-field>
              </div>

              <div class="validation-step">
                <p class="w-72 m-0">
                  7. Validação de correções
                  <span class="text-red-500">*</span>
                </p>
                <v-text-field
                  class="max-w-[200px]"
                  density="compact"
                  variant="outlined"
                  type="date"
                  :min="minDateValidacaoCorrecoes"
                  v-model="dataValidacaoCorrecoes"
                  :rules="[rules.required]"
                  hide-details="auto"
                ></v-text-field>
              </div>
            </div>
          </div>
          <div class="w-full mt-8">
            <h2 class="text-2xl font-bold">
              Definição de formulário <span class="text-red-500">*</span>
            </h2>
            <h3 class="text-lg font-medium mt-6">
              Escolha um formulário ou crie um novo
            </h3>
            <div class="flex flex-col items-start gap-4 mt-4 w-1/2">
              <v-autocomplete
                class="w-1/2"
                variant="solo"
                density="compact"
                :items="formularios"
                item-title="nome"
                item-value="id"
                v-model="formularioSelecionado"
                :rules="[rules.required]"
                placeholder="Pesquise e selecione um formulário"
              ></v-autocomplete>
              <button
                type="button"
                @click.prevent="criarNovoFormulario"
                class="flex items-center justify-center gap-2 px-6 py-3 bg-[#0b3d6e] rounded-[15px] text-[#ebebeb] border border-[#0b3d6e] hover:bg-[#082d52] transition-colors duration-200 text-[15px] font-medium whitespace-nowrap h-[44px]"
              >
                Criar novo formulário
              </button>
            </div>
          </div>
        </div>
      </v-form>
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="2500"
        location="top right"
      >
        {{ snackbar.text }}
      </v-snackbar>
    </v-container>

    <!-- Modal de Seleção de Projetos -->
    <ModalSelecionarProjetos
      v-model="projectModal"
      v-model:selected="projetosSelecionados"
      :projetos="projetos"
    />
  </div>
</template>

<script>
/**
 * @module CreateValidationPeriod
 * @desc Componente para criar um período de validação.
 * @vue-data {Boolean} [isFormValid=false] Flag que indica se o formulário inteiro é válido
 * @vue-data {String} [nomeValidacao=''] Nome da validação
 * @vue-data {String} [dataPreparacao=null] Data final para a etapa de preparação
 * @vue-data {String} [dataValidacaoRoteiros=null] Data final para validação de roteiros
 * @vue-data {String} [dataExecucao=null] Data final para execução da validação
 * @vue-data {String} [dataIdentificacaoPendencias=null] Data final para identificação de pendências
 * @vue-data {String} [dataPriorizacaoPendencias=null] Data final para priorização de pendências
 * @vue-data {String} [dataCorrecoes=null] Data final para correções
 * @vue-data {String} [dataValidacaoCorrecoes=null] Data final para validação de correções
 * @vue-data {Boolean} [validaRoteiros=true] Flag que habilita a etapa de roteiros
 * @vue-data {Array.<Number>} [projetosSelecionados=[]] Lista de IDs dos projetos selecionados
 * @vue-data {String} [searchQuery=''] Termo de pesquisa para a seleção de projetos
 * @vue-data {Number} [formularioSelecionado=null] ID do formulário de avaliação selecionado
 * @vue-data {Object} [rules] Objeto com as regras de validação dos campos
 * @vue-data {Array.<Object>} [formularios] Dados mockados dos formulários disponíveis
 * @vue-data {Array.<Object>} [projetos] projetos disponíveis para validação
 * @vue-computed {String} minDate - Retorna a data de "amanhã" no formato YYYY-MM-DD para limitar os calendários
 */
import axios from 'axios';

import ModalSelecionarProjetos from '@/components/dialogs/ModalSelecionarProjetos.vue';

const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000';

function addOneDay(dateStr) {
  if (!dateStr) {
    return null;
  }
  const [year, month, day] = dateStr.split('-');
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + 1);
  const newYear = date.getFullYear();
  const newMonth = String(date.getMonth() + 1).padStart(2, '0');
  const newDay = String(date.getDate()).padStart(2, '0');

  return `${newYear}-${newMonth}-${newDay}`;
}

export default {
  name: 'CreateValidationPeriod',
  components: {
    ModalSelecionarProjetos,
  },

  computed: {
    /**
     * Calcula o título da página
     * @returns {String} Título da página
     */
    pageTitle() {
      return this.isEditing ? 'Editar Validação' : 'Nova Validação';
    },
    /**
     * Calcula o título do botão
     * @returns {String} Título do botão
     */
    buttonTitle() {
      return this.isEditing ? 'Finalizar Edição' : 'Salvar Validação';
    },
    minDate() {
      // Cria a data de "amanhã" baseada no fuso horário local
      const data = new Date();
      data.setDate(data.getDate() + 1);

      const ano = data.getFullYear();
      const mes = String(data.getMonth() + 1).padStart(2, '0');
      const dia = String(data.getDate()).padStart(2, '0');

      return `${ano}-${mes}-${dia}`;
    },
    minDateValidacaoRoteiros() {
      return addOneDay(this.dataPreparacao) || this.minDate;
    },
    minDateExecucao() {
      return (
        addOneDay(
          this.validaRoteiros
            ? this.dataValidacaoRoteiros
            : this.dataPreparacao,
        ) || this.minDate
      );
    },
    minDateIdentificacao() {
      return addOneDay(this.dataExecucao) || this.minDate;
    },
    minDatePriorizacao() {
      return addOneDay(this.dataIdentificacaoPendencias) || this.minDate;
    },
    minDateCorrecoes() {
      return addOneDay(this.dataPriorizacaoPendencias) || this.minDate;
    },
    minDateValidacaoCorrecoes() {
      return addOneDay(this.dataCorrecoes) || this.minDate;
    },
  },
  data() {
    return {
      loading: false,
      loadingSubmit: false,
      snackbar: {
        show: false,
        text: '',
        color: 'success',
      },
      isEditing: false,
      statusAtual: 'EDICAO',
      isFormValid: false,
      nomeValidacao: '',
      dataPreparacao: null,
      dataValidacaoRoteiros: null,
      dataExecucao: null,
      dataIdentificacaoPendencias: null,
      dataPriorizacaoPendencias: null,
      dataCorrecoes: null,
      dataValidacaoCorrecoes: null,
      validaRoteiros: true,
      projetosSelecionados: [],
      projectModal: false,
      formularioSelecionado: null,
      rules: {
        required: (value) => !!value || 'Campo obrigatório',
        requiredArray: (value) =>
          (value && value.length > 0) || 'Selecione pelo menos um projeto',
      },
      // Dados mockados para testar a interface, futuramente esses dados devem vir da API
      formularios: [],
      projetos: [],
    };
  },
  async mounted() {
    const promises = [this.carregarProjetos(), this.carregarFormularios()];

    // Restaurar estado salvo (se houver) ao voltar da tela de criar formulário
    const savedState = sessionStorage.getItem('validationPeriodState');
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        this.nomeValidacao = parsed.nomeValidacao;
        this.dataPreparacao = parsed.dataPreparacao;
        this.dataValidacaoRoteiros = parsed.dataValidacaoRoteiros;
        this.dataExecucao = parsed.dataExecucao;
        this.dataIdentificacaoPendencias = parsed.dataIdentificacaoPendencias;
        this.dataPriorizacaoPendencias = parsed.dataPriorizacaoPendencias;
        this.dataCorrecoes = parsed.dataCorrecoes;
        this.dataValidacaoCorrecoes = parsed.dataValidacaoCorrecoes;
        this.validaRoteiros = parsed.validaRoteiros;
        this.projetosSelecionados = parsed.projetosSelecionados;
        this.formularioSelecionado = parsed.formularioSelecionado;
      } catch (e) {
        console.error('Erro ao restaurar estado:', e);
      }
      sessionStorage.removeItem('validationPeriodState');
    }

    if (this.$route.query.newFormId) {
      this.formularioSelecionado = Number(this.$route.query.newFormId);
    }

    const id = this.$route.params.id;
    if (id) {
      this.isEditing = true;
      promises.push(this.carregarValidacao(id));
    }

    this.loading = true;
    try {
      await Promise.all(promises);
    } catch (error) {
      console.error('Erro no carregamento inicial:', error);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    /**
     * Carrega os projetos do backend.
     */
    carregarProjetos() {
      return axios
        .get(`${apiUrl}/projetos/COORDENADOR`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        })
        .then((response) => {
          this.projetos = response.data.filter((p) => !p.flencerrado);
        })
        .catch((error) => {
          console.error('Erro ao carregar projetos:', error);
        });
    },
    async carregarFormularios() {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${apiUrl}/api/v1/formularioValidacao`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        this.formularios = response.data
          .filter((f) => f.fl_ativo !== false)
          .map((f) => ({
            id: f.id_formulario_validacao,
            nome: f.titulo,
          }));
      } catch (error) {
        console.error('Erro ao carregar formulários:', error);
      }
    },
    criarNovoFormulario() {
      sessionStorage.setItem(
        'validationPeriodState',
        JSON.stringify(this.$data),
      );
      this.$router.push({
        path: '/formulario-validacao/novo',
        query: { origin: this.$route.fullPath },
      });
    },
    /**
     * Formata a data.
     * @param {string} dateStr - Data no formato string
     * @returns {string} - Data no formato YYYY-MM-DD
     */
    formatDate(dateStr) {
      if (!dateStr) {
        return null;
      }

      return new Date(dateStr).toISOString().split('T')[0];
    },
    /**
     * Carrega a validação do backend.
     * @param {number} id - ID da validação
     */
    async carregarValidacao(id) {
      try {
        const response = await axios.get(`${apiUrl}/api/v1/validacoes/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const data = response.data;
        this.statusAtual = data.situacao || 'EDICAO';
        this.nomeValidacao = data.nome_validacao;
        this.formularioSelecionado = data.id_formulario;
        this.dataPreparacao = this.formatDate(data.data_final_preparacao);

        this.validaRoteiros = data.flag_validacao_roteiro;
        this.dataValidacaoRoteiros = this.formatDate(
          data.data_final_validacao_roteiro,
        );

        this.dataExecucao = this.formatDate(data.data_final_execucao_validacao);
        this.dataIdentificacaoPendencias = this.formatDate(
          data.data_final_identificacao_pendencias,
        );
        this.dataPriorizacaoPendencias = this.formatDate(
          data.data_final_priorizacao_pendencias,
        );
        this.dataCorrecoes = this.formatDate(data.data_final_correcoes);
        this.dataValidacaoCorrecoes = this.formatDate(
          data.data_final_validacao_correcoes,
        );

        this.projetosSelecionados = data.tb_projetos_envolvidos.map(
          (p) => p.id_projeto,
        );
      } catch (error) {
        console.error('Erro ao carregar validação:', error);
      }
    },
    /**
     * Retorna para a página anterior.
     */
    goBack() {
      this.$router.go(-1);
    },
    getProjectName(id) {
      const proj = this.projetos.find((p) => p.id_projeto === id);

      return proj ? proj.nome : `Projeto ${id}`;
    },
    removerProjeto(id) {
      this.projetosSelecionados = this.projetosSelecionados.filter(
        (pId) => pId !== id,
      );
    },
    openProjectModal() {
      this.projectSearchQuery = '';
      this.projectModal = true;
    },
    closeProjectModal() {
      this.projectModal = false;
    },

    /**
     * Envia a validação para o backend.
     * @param {string} situacao - Situação da validação
     */
    async submitValidation(situacaoParam) {
      if (!this.nomeValidacao) {
        this.snackbar.text = 'O nome da validação é obrigatório.';
        this.snackbar.color = 'error';
        this.snackbar.show = true;

        return;
      }

      if (situacaoParam === 'PREPARACAO' && !this.isFormValid) {
        this.snackbar.text =
          'Preencha todos os campos obrigatórios para publicar.';
        this.snackbar.color = 'error';
        this.snackbar.show = true;

        return;
      }

      this.loadingSubmit = true;

      let situacaoFinal = situacaoParam;

      if (situacaoFinal === 'PREPARACAO') {
        const date = new Date();
        const ano = date.getFullYear();
        const mes = String(date.getMonth() + 1).padStart(2, '0');
        const dia = String(date.getDate()).padStart(2, '0');
        const nowStr = `${ano}-${mes}-${dia}`;

        if (
          this.dataValidacaoCorrecoes &&
          nowStr > this.dataValidacaoCorrecoes
        ) {
          situacaoFinal = 'VALIDACAO_FINALIZADA';
        } else if (this.dataCorrecoes && nowStr > this.dataCorrecoes) {
          situacaoFinal = 'VALIDACAO_CORRECOES';
        } else if (
          this.dataPriorizacaoPendencias &&
          nowStr > this.dataPriorizacaoPendencias
        ) {
          situacaoFinal = 'CORRECOES';
        } else if (
          this.dataIdentificacaoPendencias &&
          nowStr > this.dataIdentificacaoPendencias
        ) {
          situacaoFinal = 'PRIORIZACAO_PENDENCIAS';
        } else if (this.dataExecucao && nowStr > this.dataExecucao) {
          situacaoFinal = 'IDENTIFICACAO_PENDENCIAS';
        } else if (
          this.validaRoteiros &&
          this.dataValidacaoRoteiros &&
          nowStr > this.dataValidacaoRoteiros
        ) {
          situacaoFinal = 'USUARIOS_TESTANDO';
        } else if (this.dataPreparacao && nowStr > this.dataPreparacao) {
          situacaoFinal = this.validaRoteiros
            ? 'VALIDACAO_ROTEIROS'
            : 'USUARIOS_TESTANDO';
        } else {
          situacaoFinal = 'PREPARACAO';
        }
      }

      const payload = {
        nome: this.nomeValidacao,
        projetosIds: this.projetosSelecionados,
        formularioId: this.formularioSelecionado,
        situacao: situacaoFinal,
        etapas: {
          dataPreparacao: this.dataPreparacao,
          validaRoteiros: this.validaRoteiros,
          dataValidacaoRoteiros: this.validaRoteiros
            ? this.dataValidacaoRoteiros
            : null,
          dataExecucao: this.dataExecucao,
          dataIdentificacaoPendencias: this.dataIdentificacaoPendencias,
          dataPriorizacaoPendencias: this.dataPriorizacaoPendencias,
          validaEtapaCorrecoes: true,
          dataCorrecoes: this.dataCorrecoes,
          dataValidacaoCorrecoes: this.dataValidacaoCorrecoes,
        },
      };

      console.log('🚀 Payload pronto para envio:', payload);

      try {
        const config = {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        };

        if (this.isEditing) {
          await axios.put(
            `${apiUrl}/api/v1/validacoes/${this.$route.params.id}`,
            payload,
            config,
          );
          this.snackbar.text = 'Validação atualizada com sucesso!';
          this.snackbar.color = 'success';
        } else {
          await axios.post(`${apiUrl}/api/v1/validacoes`, payload, config);
          this.snackbar.text = 'Validação salva com sucesso!';
          this.snackbar.color = 'success';
        }

        this.snackbar.show = true;

        // Espera o toast ser exibido antes de redirecionar
        setTimeout(() => {
          this.$router.push('/validations');
        }, 1500);
      } catch (error) {
        console.error('Erro ao salvar validação:', error);
        this.snackbar.text =
          error.response?.data?.message ||
          'Ocorreu um erro ao salvar a validação.';
        this.snackbar.color = 'error';
        this.snackbar.show = true;
      } finally {
        this.loadingSubmit = false;
      }
    },
  },
};
</script>

<style scoped>
.validation-step {
  @apply flex items-center gap-4 bg-white p-3 rounded-xl shadow-sm border border-gray-100;
}

.text-input {
  @apply bg-white p-2;
}
</style>
